import type { DateRelation } from '@frontend/api';

import RelativeOrSingleDatePicker from 'components/listTablePage/searchAndFilters/components/filters/components/DateRelationFilter/RelativeOrSingleDatePicker';

import AmountAndUnitInput from './AmountAndUnitInput';
import DateRangeInput from './DateRangeInput';
import SingleDateInput from './SingleDateInput';
import UnitOnlyInput from './UnitOnlyInput';
import { AMOUNT_AND_UNIT_DATE_RELATIONS } from './const/amountAndUnitDateRelations.const';
import { DATE_RANGE_DATE_RELATIONS } from './const/dateRangeDateRelations.const';
import { DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP } from './const/dateUnitPluralToDefaultAmountMap.const';
import { SET_UNIT_DATE_RELATIONS } from './const/setUnitDateRelations.const';
import { SINGLE_DATE_DATE_RELATIONS } from './const/singleDateDateRelations.const';
import { UNIT_ONLY_DATE_RELATIONS } from './const/unitOnlyDateRelations.const';
import type {
  AmountAndUnitRelationFilterValue,
  DateRangeRelationFilterValue,
  DateRangeValue,
  ExcludableDateRelation,
  RelationFilterWorkingValue,
  SetUnitRelationFilterValue,
  SingleDateRelationFilterValue,
  SingleDateValue,
  TimeUnitPlural,
  UnitOnlyRelationFilterValue,
} from './types';

export const valueUsesAmountAndUnitInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
): relationFilterValue is AmountAndUnitRelationFilterValue<ExcludedRelations> =>
  (AMOUNT_AND_UNIT_DATE_RELATIONS as readonly DateRelation[]).includes(
    relationFilterValue.relation,
  );

export const valueUsesUnitOnlyInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
): relationFilterValue is UnitOnlyRelationFilterValue<ExcludedRelations> =>
  (UNIT_ONLY_DATE_RELATIONS as readonly DateRelation[]).includes(
    relationFilterValue.relation,
  );

export const valueUsesSetUnitInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
): relationFilterValue is SetUnitRelationFilterValue<ExcludedRelations> =>
  (SET_UNIT_DATE_RELATIONS as readonly DateRelation[]).includes(
    relationFilterValue.relation,
  );

export const valueUsesSingleDateInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
): relationFilterValue is SingleDateRelationFilterValue<ExcludedRelations> =>
  (SINGLE_DATE_DATE_RELATIONS as readonly DateRelation[]).includes(
    relationFilterValue.relation,
  );

export const valueUsesDateRangeInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
): relationFilterValue is DateRangeRelationFilterValue<ExcludedRelations> =>
  (DATE_RANGE_DATE_RELATIONS as readonly DateRelation[]).includes(
    relationFilterValue.relation,
  );

export const renderAmountAndUnitInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void,
  isPopoverOverlayEnabled?: boolean,
) =>
  valueUsesAmountAndUnitInput(relationFilterValue) ? (
    <AmountAndUnitInput
      relationFilterValue={relationFilterValue}
      isPopoverOverlayEnabled={isPopoverOverlayEnabled}
      onAmountChange={(newAmount: string) =>
        onChange({
          ...relationFilterValue,
          amount: newAmount,
        })
      }
      onUnitChange={(newUnit: TimeUnitPlural) => {
        const newFilterValue: RelationFilterWorkingValue<ExcludedRelations> = {
          ...relationFilterValue,
          unit: newUnit,
          amount: DATE_UNIT_PLURAL_TO_DEFAULT_AMOUNT_MAP[newUnit],
        };

        onChange(newFilterValue);
      }}
    />
  ) : null;

export const renderUnitOnlyInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void,
  isPopoverOverlayEnabled?: boolean,
) =>
  valueUsesUnitOnlyInput(relationFilterValue) ? (
    <UnitOnlyInput
      relationFilterValue={relationFilterValue}
      onChange={(newUnit) =>
        onChange({
          relation: relationFilterValue.relation,
          unit: newUnit,
        })
      }
      isPopoverOverlayEnabled={isPopoverOverlayEnabled}
    />
  ) : null;

export const renderSingleDateInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void,
  dateTimeFormat: string,
  showTimeSettings: object | boolean,
) =>
  valueUsesSingleDateInput(relationFilterValue) ? (
    <SingleDateInput
      relationFilterValue={relationFilterValue}
      onChange={(selectedDate: SingleDateValue) =>
        onChange({
          ...relationFilterValue,
          startDate: selectedDate,
        })
      }
      dateTimeFormat={dateTimeFormat}
      showTimeSettings={showTimeSettings}
    />
  ) : null;

export const renderRelativeOrSingleDatePicker = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  blockFutureSelections: boolean,
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void,
  dateTimeFormat: string,
  showTimeSettings: object | boolean,
  isPopoverOverlayEnabled?: boolean,
) =>
  valueUsesSingleDateInput(relationFilterValue) ? (
    <RelativeOrSingleDatePicker
      relationFilterValue={relationFilterValue}
      onStartDateChange={(selectedDate: SingleDateValue) =>
        onChange({
          ...relationFilterValue,
          startDate: selectedDate,
        })
      }
      onRelativeDateChange={(relativeDate: string) =>
        onChange({
          ...relationFilterValue,
          relativeDate: relativeDate,
          startDate: null,
        })
      }
      dateTimeFormat={dateTimeFormat}
      showTimeSettings={showTimeSettings}
      blockFutureSelections={blockFutureSelections}
      isPopoverOverlayEnabled={isPopoverOverlayEnabled}
    />
  ) : null;

export const renderDateRangeInput = <
  ExcludedRelations extends ExcludableDateRelation,
>(
  relationFilterValue: RelationFilterWorkingValue<ExcludedRelations>,
  onChange: (value: RelationFilterWorkingValue<ExcludedRelations>) => void,
) =>
  valueUsesDateRangeInput(relationFilterValue) ? (
    <DateRangeInput
      relationFilterValue={relationFilterValue}
      onChange={(newDateRange: DateRangeValue) => {
        const newFilterValue: RelationFilterWorkingValue<ExcludedRelations> = {
          ...relationFilterValue,
          ...newDateRange,
        };

        onChange(newFilterValue);
      }}
    />
  ) : null;
